import { Instagram, MessageCircle, MapPin, Clock, Mail } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LOGO } from "@/lib/constants";
import { generalWaLink } from "@/lib/whatsapp";

const SECTION_IDS = { home: "hero", about: "about", services: "services", prices: "services", institute: "institute", contact: "contact" };

export default function Footer({ contact }) {
  const { t, lang, setLang, langs } = useLang();
  const info = contact || {};
  const loc = info[lang] || info.fr || {};
  const waLink = generalWaLink(info.phone_intl || "972555004933", lang);
  const goto = (key) => document.getElementById(SECTION_IDS[key])?.scrollIntoView({ behavior: "smooth" });
  const year = new Date().getFullYear();

  return (
    <footer className="bg-ink text-ivory/90" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-16">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="L'Éclat du Regard" className="h-14 w-14 rounded-full object-cover ring-1 ring-gold/50" />
              <span className="font-serif text-2xl">{t.hero.brand}</span>
            </div>
            <p className="mt-5 max-w-sm text-sm text-ivory/60 leading-relaxed">{t.footer.tagline}</p>
            <div className="mt-6 flex gap-3">
              <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-gold transition-colors" data-testid="footer-whatsapp" aria-label="WhatsApp">
                <MessageCircle className="h-5 w-5" />
              </a>
              <a href={info.instagram_url} target="_blank" rel="noopener noreferrer" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-gold transition-colors" data-testid="footer-instagram" aria-label="Instagram">
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{t.footer.nav}</h4>
            <ul className="space-y-2.5 text-sm text-ivory/70">
              {["home", "about", "services", "institute", "contact"].map((s) => (
                <li key={s}><button onClick={() => goto(s)} className="hover:text-gold transition-colors" data-testid={`footer-nav-${s}`}>{t.nav[s]}</button></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{t.footer.contact}</h4>
            <ul className="space-y-3 text-sm text-ivory/70">
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-gold shrink-0" /> {loc.address}</li>
              <li className="flex items-center gap-2"><MessageCircle className="h-4 w-4 text-gold shrink-0" /><span dir="ltr">{info.phone_display}</span></li>
              {info.email && <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-gold shrink-0" /><a href={`mailto:${info.email}`} className="hover:text-gold transition-colors" data-testid="footer-email">{info.email}</a></li>}
              <li className="flex items-start gap-2"><Clock className="h-4 w-4 mt-0.5 text-gold shrink-0" /> {loc.hours}</li>
            </ul>
            <div className="mt-5">
              <p className="text-xs uppercase tracking-[0.2em] text-gold mb-2">{t.footer.language}</p>
              <div className="flex gap-2">
                {langs.map((l) => (
                  <button key={l.code} onClick={() => setLang(l.code)}
                    className={`rounded-full px-3 py-1 text-xs border transition-colors ${lang === l.code ? "bg-gold border-gold text-white" : "border-white/20 text-ivory/70 hover:border-gold"}`}
                    data-testid={`footer-lang-${l.code}`}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-ivory/50">
          <p>© {year} L'Éclat du Regard. {t.footer.rights}</p>
          <div className="flex items-center gap-5">
            <span>{t.footer.legal}</span>
            <span>{t.footer.privacy}</span>
            <span className="text-gold">{t.footer.byAppointment}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
